import type { ContentAdapter, ContentFormData } from "../hooks/useContentEditor";
import { normalizeSubmission } from "../hooks/useContentEditor";
import type { UpdateSubmissionRequest } from "../hooks/useSubmissionMutations";
import type { Submission } from "../hooks/useSubmissions";

/**
 * Convert ContentFormData to Submission update payload
 */
export function contentToSubmissionUpdate(data: Partial<ContentFormData>): UpdateSubmissionRequest {
  console.log("[submissionAdapter] contentToSubmissionUpdate input data:", data);
  console.log("[submissionAdapter] horizontalTileUrl:", data.horizontalTileUrl);
  console.log("[submissionAdapter] verticalTileUrl:", data.verticalTileUrl);

  // Build crew array
  const crew = data.crew?.map((c) => ({
    name: c.name,
    job: c.job,
    department: c.department,
  }));

  // Convert language field to languages array (same as titleAdapter)
  const languages = Array.isArray(data.language) && data.language.length > 0 ? data.language : data.languages || [];

  const result = {
    title: data.title,
    releaseYear: data.releaseYear,
    runtime: data.runtime,
    synopsis: data.synopsis,
    description: data.description,
    director: data.director,
    cast: data.cast,
    crew,
    genres: data.genres,
    rating: data.rating,
    languages: languages,
    country: data.country,
    posterUrl: data.posterUrl,
    backdropUrl: data.backdropUrl,
    logoUrl: data.logoUrl,
    horizontalTileUrl: data.horizontalTileUrl,
    verticalTileUrl: data.verticalTileUrl,
    featureSubtitles: data.featureSubtitles,
    trailerSubtitles: data.trailerSubtitles,
  };

  console.log("[submissionAdapter] contentToSubmissionUpdate output:", result);
  return result;
}

/**
 * Create a Submission adapter for the ContentEditor
 */
export function createSubmissionAdapter(
  submissionId: string,
  loading: boolean,
  error: string | null,
  refetch: () => Promise<{ data: Submission | null }>,
  updateSubmission: (id: string, data: UpdateSubmissionRequest) => Promise<Submission | null>,
  submitForReview: (id: string) => Promise<Submission | null>,
  resubmit: (id: string) => Promise<Submission | null>,
): ContentAdapter {
  return {
    mode: "submission",

    async load(_id: string): Promise<ContentFormData | null> {
      const result = await refetch();
      if (!result.data) return null;
      return normalizeSubmission(result.data);
    },

    async save(id: string, data: Partial<ContentFormData>): Promise<void> {
      const updates = contentToSubmissionUpdate(data);
      await updateSubmission(id, updates);
    },

    async submit(id: string): Promise<void> {
      // Fetch fresh submission data to get current status
      const result = await refetch();
      const submission = result.data;

      // Use resubmit endpoint for REJECTED submissions, submit for DRAFT
      if (submission?.status === "REJECTED") {
        await resubmit(id);
      } else {
        await submitForReview(id);
      }
    }, // Providers can only edit DRAFT or REJECTED submissions
    canEdit(data: ContentFormData): boolean {
      return data.status === "DRAFT" || data.status === "REJECTED";
    },

    // Get display title with status
    getTitle(data: ContentFormData): string {
      const status = data.status ? ` (${data.status})` : "";
      return `${data.title}${status}`;
    },
  };
}
